import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';

const Onboarding = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    age: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/', { replace: true });
    }
    
    if (user && !loading) {
      // Check if profile is already complete and prefill from metadata
      checkProfileCompletion();
    }
  }, [user, loading, navigate]);

  const checkProfileCompletion = async () => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('first_name, last_name, age')
        .eq('user_id', user.id)
        .single();

      // Prefill form data from existing profile or user metadata
      const metadata = user.user_metadata || {};
      const prefillData = {
        first_name: profile?.first_name || metadata.given_name || metadata.first_name || (metadata.full_name?.split(' ')[0] ?? ''),
        last_name: profile?.last_name || metadata.family_name || metadata.last_name || (metadata.full_name?.split(' ').slice(1).join(' ') ?? ''),
        age: profile?.age?.toString() || ''
      };
      
      setFormData(prefillData);

      // If profile is complete, redirect to dashboard
      if (profile?.age && (profile?.first_name || profile?.last_name)) {
        navigate('/dashboard', { replace: true });
      } else if (prefillData.first_name && prefillData.last_name && !prefillData.age) {
        // Skip name step if we have names from OAuth
        setStep(2);
      }
    } catch (error) {
      console.log('No existing profile found, continuing with onboarding');
      // Still try to prefill from metadata
      const metadata = user.user_metadata || {};
      const prefillData = {
        first_name: metadata.given_name || metadata.first_name || (metadata.full_name?.split(' ')[0] ?? ''),
        last_name: metadata.family_name || metadata.last_name || (metadata.full_name?.split(' ').slice(1).join(' ') ?? ''),
        age: ''
      };
      setFormData(prefillData);
      
      if (prefillData.first_name && prefillData.last_name) {
        setStep(2); // Skip to age if we have names
      }
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (!formData.first_name.trim() || !formData.last_name.trim()) {
        toast({
          title: "Required fields",
          description: "Please enter both first and last name.",
          variant: "destructive",
        });
        return;
      }
      setStep(2);
    } else if (step === 2) {
      handleSubmit();
    }
  };

  const handleSubmit = async () => {
    const age = parseInt(formData.age);
    if (!age || age < 18 || age > 100) {
      toast({
        title: "Invalid age",
        description: "Please enter a valid age between 18 and 100.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .upsert({
          user_id: user.id,
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          age: age,
          onboarding_completed: true,
          updated_at: new Date().toISOString()
        }, { onConflict: 'user_id' });

      if (error) throw error;

      toast({
        title: "Profile complete!",
        description: "Welcome to Safurra!",
      });

      navigate('/dashboard', { replace: true });
    } catch (error: any) {
      console.error('Error updating profile:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save profile.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle>Complete Your Profile</CardTitle>
          <CardDescription>
            Help us personalize your experience
          </CardDescription>
          <Progress value={step * 50} className="mt-4" />
        </CardHeader>
        <CardContent className="space-y-4">
          {step === 1 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  placeholder="Enter your first name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  placeholder="Enter your last name"
                />
              </div>
            </>
          )}

          {step === 2 && (
            <div className="space-y-2">
              <Label htmlFor="age">Age</Label>
              <Input
                id="age"
                type="number"
                min="18"
                max="100"
                value={formData.age}
                onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                placeholder="Enter your age"
              />
              <p className="text-sm text-muted-foreground">
                Must be between 18 and 100 years old
              </p>
            </div>
          )}

          <div className="flex gap-2 pt-4">
            {step > 1 && (
              <Button
                variant="outline"
                onClick={() => setStep(step - 1)}
                disabled={isSubmitting}
                className="flex-1"
              >
                Back
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={isSubmitting}
              className="flex-1"
            >
              {step === 2 ? (isSubmitting ? 'Saving...' : 'Complete') : 'Next'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Onboarding;